import { createContext, useContext, ReactNode, Context } from 'react';
import { doc, FirestoreDataConverter, FirestoreError } from 'firebase/firestore';
import { db } from 'firebaseStore';
import { useDocumentData } from 'react-firebase-hooks/firestore';
import { Base } from 'models';

export type DocState<T = Base> = [T | undefined, boolean, FirestoreError | undefined];

const getKey = (collectionName: string, id: string) => `${collectionName}-id:${id}`;
const states: { [key: string]: Context<DocState | undefined> | undefined } = {};

export const getDocumentContext = (collectionName: string, id: string) => {
    const key = getKey(collectionName, id);
    if (!states[key]) {
        states[key] = createContext<DocState | undefined>(undefined);
    }
    return states[key] as Context<DocState | undefined>;
};

export interface DocumentProviderProps<T> {
    converter: FirestoreDataConverter<T>;
    collectionName: string;
    id: string;
    children: ReactNode | undefined;
}
export const DocumentProvider = <T extends Base>({
    children,
    collectionName,
    id,
    converter,
}: DocumentProviderProps<T>) => {
    const ActualContext = getDocumentContext(collectionName, id);
    const docRef = doc(db, collectionName, id).withConverter(converter);
    const docState = useDocumentData(docRef, { idField: 'id' });
    return <ActualContext.Provider value={docState}>{children}</ActualContext.Provider>;
};

export function useDocumentContext(collectionName: string, id: string) {
    const context = useContext(getDocumentContext(collectionName, id));
    if (context === undefined) {
        throw new Error('useDocumentContext must be used within a CollectionProvider');
    }
    return context;
}
export function useOptionalDocumentContext(collectionName: string, id: string) {
    const context = useContext(getDocumentContext(collectionName, id));
    return context || [undefined, false, undefined];
}
